/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { format, getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IAaaAccountSaveddesignTileData } from './aaa_account-saveddesign-tile.data';
import { IAaa_accountSaveddesignTileProps } from './aaa_account-saveddesign-tile.props.autogenerated';
import DescriptionComponent from './components/aaa_account-saveddesign-tile-description';
import LinksComponent from './components/aaa_account-saveddesign-tile-links';
import { getSavedDesignsAsync } from '../../dataActions/DataActionExtension.g';

export interface IAccountWishlistTileViewProps extends IAaa_accountSaveddesignTileProps<IAaaAccountSaveddesignTileData> {
    AccountWishlistTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
    description?: React.ReactNode;
}

/**
 *
 * AccountWishlist component.
 * @extends {React.PureComponent<IAaa_accountSaveddesignTileProps<IAaaAccountSaveddesignTileData >>}
 */
class AccountSavedDesignTile extends React.PureComponent<IAaa_accountSaveddesignTileProps<IAaaAccountSaveddesignTileData>, any> {
    private readonly telemetryContent?: ITelemetryContent;

    constructor(props: IAaa_accountSaveddesignTileProps<IAaaAccountSaveddesignTileData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.state = {
            savedDesignCount: 0
        };
    }
    public componentDidMount() {
        void this.getSavedDesignCount();
    }

    private readonly getSavedDesignCount = async () => {
        const { context } = this.props;
        if (context.actionContext.requestContext.user.isAuthenticated) {
            const savedDesigns: any = await getSavedDesignsAsync({
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            });
            this.setState({ savedDesignCount: savedDesigns.Result.length });
        }
    };
    public render(): JSX.Element | null {
        const { config, resources } = this.props;
        const { className, heading, links } = config;
        const { accountSavedDesignTileDescription } = resources;
        const description = format(accountSavedDesignTileDescription || 'You have {0} items in the Wishlist.', this.state.savedDesignCount);

        const viewProps = {
            ...this.props,
            className,
            AccountWishlistTile: {
                moduleProps: this.props,
                className: classnames('ms-account-saveddesign-tile', config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className='ms-account-saveddesign-tile__heading'
                    tag={heading.tag}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: links && links.length > 0 && (
                <LinksComponent
                    {...{
                        links,
                        onTextChange: this.handleLinkTextChange,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent
                    }}
                />
            ),
            description: <DescriptionComponent description={description} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default AccountSavedDesignTile;
